import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  Paper,
  Divider,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import useAsync from '../../hooks/useAsync';
import Editor from '../common/Editor';
import ModernButton from '../common/ModernButton';
import ModernTextField from '../common/ModernTextField';
import { useToast } from '../../contexts/ToastContext';
import {
  QuestionTypes,
  hideCommonQuestionTypes,
  hideAnswerOptionsTypes,
  parseIntervalToCount,
  formatAnswerOptions,
  createOptionObjects,
  stripHtml,
} from '../../constants/questionTypes';

/**
 * QuestionSetFormBase - Shared form component for Reading and Listening question sets
 *
 * @param {Object} props
 * @param {string} props.parentId - Reading ID or Listening ID
 * @param {string} props.questionSetId - Question Set ID (for edit mode)
 * @param {string} props.section - Current section (passage1/part1 etc)
 * @param {string} props.sectionKey - Key name for section ('passage' or 'part')
 * @param {Array} props.sectionOptions - Available sections (e.g., ['passage1', 'passage2', 'passage3'])
 * @param {string} props.sectionLabel - Label for section selector (e.g., 'Passage' or 'Part')
 * @param {React.ReactNode} props.icon - Icon component for header
 * @param {string} props.basePath - Base URL path (e.g., '/readings' or '/listenings')
 * @param {Object} props.api - API functions { getQuestionSetById, createQuestionSet, updateQuestionSet, deleteQuestionSet, getAllQuestionSets }
 * @param {Function} props.extractQuestionSetId - Function to extract question set ID from response
 */
const QuestionSetFormBase = ({
  parentId,
  questionSetId,
  section,
  sectionKey,
  sectionOptions,
  sectionLabel,
  icon: Icon,
  basePath,
  api,
  extractQuestionSetId,
}) => {
  const navigate = useNavigate();
  const isEditMode = Boolean(questionSetId);
  const editorRef = useRef();
  const instructionRef = useRef();
  const headingRef = useRef();
  const { showToast } = useToast();

  const [originalSection, setOriginalSection] = useState(section);
  const [formData, setFormData] = useState({
    instructionLabel: '',
    type: '',
    questionInterval: '',
    questionCount: 0,
    heading: '',
    commonQuestion: '',
    answerOptions: [],
    [sectionKey]: section,
  });
  const [answerOptions, setAnswerOptions] = useState([]);
  const [optionInput, setOptionInput] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      const fetchData = async () => {
        try {
          const { data } = await api.getQuestionSetById(parentId, section, questionSetId);
          setOriginalSection(data[sectionKey] || section);
          const optStrings = (data.answerOptions || []).map((opt) =>
            typeof opt === 'object' ? opt.answer.answer || opt.answer : opt
          );
          setFormData({
            ...data,
            [sectionKey]: data[sectionKey] || section,
            answerOptions: formatAnswerOptions(data.answerOptions || []),
          });
          setAnswerOptions(optStrings);
          setOptionInput('');
          instructionRef.current?.setHTML(data.instructionLabel || '');
          headingRef.current?.setHTML(data.heading || '');
          editorRef.current?.setHTML(data.commonQuestion || '');
        } catch (error) {
          console.error('Failed to fetch question set details:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isEditMode, parentId, section, questionSetId, sectionKey, api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'questionInterval') {
      const calculatedCount = parseIntervalToCount(value);
      setFormData((prev) => ({
        ...prev,
        questionInterval: value,
        questionCount: calculatedCount,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const addOptionsFromInput = (input = optionInput) => {
    const parts = input.split(',').map((t) => t.trim()).filter(Boolean);
    if (parts.length) {
      setAnswerOptions((prev) => Array.from(new Set([...prev, ...parts])));
    }
    setOptionInput('');
  };

  const { execute: submitSet, loading: saving } = useAsync(async (payload) => {
    const currentSection = formData[sectionKey];
    if (isEditMode) {
      if (currentSection !== originalSection) {
        await api.createQuestionSet(parentId, currentSection, payload);
        await api.deleteQuestionSet(parentId, originalSection, questionSetId);
      } else {
        await api.updateQuestionSet(parentId, currentSection, questionSetId, payload);
      }
    } else {
      return await api.createQuestionSet(parentId, currentSection, payload);
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    const instructionHTML = instructionRef.current?.getHTML() || '';
    const headingHTML = headingRef.current?.getHTML() || '';
    if (!stripHtml(instructionHTML)) newErrors.instructionLabel = 'Instruction is required';
    if (!formData.type) newErrors.type = 'Type is required';
    if (!formData.questionInterval.trim()) newErrors.questionInterval = 'Question interval is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const showCommonQuestion = !hideCommonQuestionTypes.includes(formData.type);
    const showAnswerOpts = !hideAnswerOptionsTypes.includes(formData.type);
    const commonQuestionHTML = showCommonQuestion ? editorRef.current?.getHTML() : '';

    let parsedOptions = null;
    if (showAnswerOpts) {
      if (answerOptions.length > 0) {
        parsedOptions = createOptionObjects(answerOptions, formData.answerOptions).filter(
          (opt) => opt.answer !== ''
        );
        if (parsedOptions.length === 0) parsedOptions = null;
      } else if (commonQuestionHTML) {
        parsedOptions = [];
      }
    }

    const payload = {
      ...formData,
      instructionLabel: instructionHTML,
      heading: headingHTML,
      commonQuestion: showCommonQuestion && stripHtml(commonQuestionHTML) ? commonQuestionHTML : null,
      answerOptions: parsedOptions != null ? formatAnswerOptions(parsedOptions) : [],
    };

    try {
      const res = await submitSet(payload);
      const currentSection = formData[sectionKey];

      if (!isEditMode) {
        let newId = extractQuestionSetId(res, parentId, currentSection);
        if (!newId) {
          try {
            const allRes = await api.getAllQuestionSets(parentId, currentSection);
            const sets = Array.isArray(allRes?.data) ? allRes.data : allRes?.data?.questionSets;
            if (Array.isArray(sets) && sets.length > 0) {
              const last = sets[sets.length - 1];
              newId = last._id || last.id || last.$oid;
              if (typeof newId === 'object') newId = newId._id || newId.id || newId.$oid;
            }
          } catch (fetchErr) {
            console.error('Failed to fetch question sets for ID fallback', fetchErr);
          }
          if (!newId) {
            showToast('Question set created, but ID was not returned.', 'warning');
            return;
          }
        }
        showToast('Question set created successfully!', 'success');
        const intervalStart = parseInt(formData.questionInterval.split('-')[0], 10) || 1;
        navigate(`${basePath}/${parentId}/${currentSection}/question-sets/${newId}/questions/new`, {
          state: {
            [sectionKey]: currentSection,
            type: formData.type,
            questionCount: formData.questionCount,
            startQuestionNumber: intervalStart,
            answerOptions: payload.answerOptions || [],
          },
        });
      } else {
        navigate(`${basePath}/${parentId}/question-sets`, { state: { [sectionKey]: currentSection } });
        showToast('Question set saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Submission error:', error);
      showToast('An error occurred. Please try again.', 'error');
    }
  };

  const showCommonQuestion = !hideCommonQuestionTypes.includes(formData.type);
  const showAnswerOpts = !hideAnswerOptionsTypes.includes(formData.type);
  const currentSection = formData[sectionKey];

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`${basePath}/${parentId}/question-sets`, { state: { [sectionKey]: currentSection } })}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            Question Set List
          </Link>
          <Typography color="text.primary">
            {isEditMode ? 'Edit Question Set' : 'Create Question Set'}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {isEditMode ? 'Edit Question Set' : 'Create Question Set'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {isEditMode
                ? `Update question set for ${currentSection}`
                : 'Set up a new question set with instructions and options'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main Form */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                    <InfoIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Basic Information
                  </Typography>
                </Box>

                {/* Section Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    Select {sectionLabel}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={currentSection}
                    onChange={(e, val) => val && handleChange({ target: { name: sectionKey, value: val } })}
                    sx={{
                      gap: 2,
                      '& .MuiToggleButton-root': {
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        py: 1.5,
                        border: '2px solid',
                        borderColor: 'grey.300',
                        margin: '0 4px',
                        minWidth: '80px',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderColor: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        },
                        '&:hover': { backgroundColor: 'primary.50', borderColor: 'primary.main' },
                      },
                    }}
                  >
                    {sectionOptions.map((opt) => (
                      <ToggleButton key={opt} value={opt}>
                        {sectionLabel} {opt.replace(/\D/g, '')}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={Boolean(errors.type)}>
                      <InputLabel>Question Type</InputLabel>
                      <Select name="type" value={formData.type} onChange={handleChange}>
                        {Object.values(QuestionTypes).map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                      {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Question Interval (e.g., 1-10)"
                      name="questionInterval"
                      value={formData.questionInterval}
                      onChange={handleChange}
                      error={Boolean(errors.questionInterval)}
                      helperText={errors.questionInterval}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Content Card */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32, mr: 2 }}>
                    <Icon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Content & Instructions
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    Instruction Label
                  </Typography>
                  <Paper sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                    <Editor ref={instructionRef} defaultValue={formData.instructionLabel} />
                  </Paper>
                  {errors.instructionLabel && (
                    <FormHelperText error sx={{ mt: 1 }}>{errors.instructionLabel}</FormHelperText>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    Heading
                  </Typography>
                  <Paper sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                    <Editor ref={headingRef} defaultValue={formData.heading} />
                  </Paper>
                </Box>
              </CardContent>
            </Card>

            {/* Common Question */}
            {showCommonQuestion && (
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32, mr: 2 }}>
                      <QuizIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Common Question
                    </Typography>
                  </Box>
                  <Paper sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                    <Editor key={formData.type} ref={editorRef} defaultValue={formData.commonQuestion} />
                  </Paper>
                </CardContent>
              </Card>
            )}

            {/* Answer Options */}
            {showAnswerOpts && (
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'error.main', width: 32, height: 32, mr: 2 }}>
                      <AddIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Answer Options
                    </Typography>
                  </Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={answerOptions}
                    inputValue={optionInput}
                    onInputChange={(e, val, reason) => {
                      if (reason === 'input') {
                        if (val.includes(',')) addOptionsFromInput(val);
                        else setOptionInput(val);
                      }
                    }}
                    onChange={(_, value) => setAnswerOptions(value)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={option}
                          sx={{ borderRadius: 2, '& .MuiChip-deleteIcon': { color: 'error.main' } }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Answer Options"
                        placeholder="Type and press Enter or comma to add"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addOptionsFromInput();
                          }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    fullWidth
                  />
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon sx={{ fontSize: 16 }} />
                      Tip: Type multiple options separated by commas, or press Enter after each option
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 3, position: 'sticky', top: 100 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Question Set Preview
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    {sectionLabel}
                  </Typography>
                  <Chip
                    label={currentSection}
                    color="primary"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Question Type
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                    {formData.type || 'Not selected'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Question Count
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {formData.questionCount || 0} questions
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <ModernButton type="submit" variant="contained" startIcon={<SaveIcon />} loading={saving} fullWidth>
                    {isEditMode ? 'Save Question Set' : 'Create Question Set'}
                  </ModernButton>
                  <ModernButton
                    onClick={() => navigate(`${basePath}/${parentId}/question-sets`, { state: { [sectionKey]: currentSection } })}
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    fullWidth
                  >
                    Back to List
                  </ModernButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default QuestionSetFormBase;
